import { Component, OnInit } from "@angular/core";
import { ConfigData } from "@calaosoft/osapp-common/config/models/ConfigData";
import { ELogActionId } from "@calaosoft/osapp-common/logging/models/ELogActionId";
import { IAppStartPauseLogData } from "@calaosoft/osapp-mobile/model/application/iapp-start-pause-log-data";
import { IConfig } from "@calaosoft/osapp-mobile/model/config/IConfig";
import { DeviceNotAuthorizedError } from "@calaosoft/osapp-mobile/model/store/error/device-not-authorized-error";
import { EStoreFlag } from "@calaosoft/osapp-mobile/model/store/EStoreFlag";
import { BatteryService } from "@calaosoft/osapp-mobile/modules/battery/services/battery.service";
import { EBTSFlag } from "@calaosoft/osapp-mobile/modules/bts/models/ebts-flag";
import { StoreBtsService } from "@calaosoft/osapp-mobile/modules/bts/services/store-bts.service";
import { EFlag } from "@calaosoft/osapp-mobile/modules/flags/models/EFlag";
import { Loader } from "@calaosoft/osapp-mobile/modules/loading/Loader";
import { LoggerService } from "@calaosoft/osapp-mobile/modules/logger/services/logger.service";
import { BarcodeReaderService } from "@calaosoft/osapp-mobile/modules/logistics/barcode-reader/services/barcode-reader.service";
import { CatalogModalMainComponent } from "@calaosoft/osapp-mobile/modules/logistics/catalog/catalog-modal-main/catalog-modal-main.component";
import { LogisticsSettings } from "@calaosoft/osapp-mobile/modules/logistics/settings/models/logistics-settings";
import { LogisticsSettingsService } from "@calaosoft/osapp-mobile/modules/logistics/settings/services/logistics-settings.service";
import { ModalComponentsRegistry } from "@calaosoft/osapp-mobile/modules/modal/model/modal-components-registry";
import { ENetworkFlag } from "@calaosoft/osapp-mobile/modules/network/models/enetwork-flag";
import { PatchService } from "@calaosoft/osapp-mobile/modules/patch/services/patch.service";
import { PwaUpdateService } from "@calaosoft/osapp-mobile/modules/pwa/services/pwa-update.service";
import { AppComponentBase } from "@calaosoft/osapp-mobile/modules/utils/components/app-component-base";
import { ApplicationService } from "@calaosoft/osapp-mobile/services/application.service";
import { BackgroundTaskService } from "@calaosoft/osapp-mobile/services/backgroundTask.service";
import { ConfigService } from "@calaosoft/osapp-mobile/services/config.service";
import { FlagService } from "@calaosoft/osapp-mobile/services/flag.service";
import { ShowMessageParamsPopup } from "@calaosoft/osapp-mobile/services/interfaces/ShowMessageParamsPopup";
import { LoadingService } from "@calaosoft/osapp-mobile/services/loading.service";
import { PlatformService } from "@calaosoft/osapp-mobile/services/platform.service";
import { UiMessageService } from "@calaosoft/osapp-mobile/services/uiMessage.service";
import { MenuController } from "@ionic/angular";
import { AlertButton } from "@ionic/core";
import { EMPTY, Observable, defer, firstValueFrom, of } from "rxjs";
import { defaultIfEmpty, finalize, mergeMap, take, takeUntil, tap } from "rxjs/operators";
import packageJson from "../../package.json";
import * as constants from "../config";
import { ArticleCatalogUpdateTask } from "../models/background-tasks/article-catalog-update-task";
import { IArticleCatalogUpdateTaskParams } from "../models/background-tasks/IArticleCatalogUpdateTaskParams";
import { IStockUpdateTaskParams } from "../models/background-tasks/istock-update-task-params";
import { RemoveObsoleteCustomerHistoryTask } from "../models/background-tasks/remove-obsolete-customer-history-task";
import { StockUpdateTask } from "../models/background-tasks/stock-update-task";
import { EMerchFlag } from "../models/emerch-flag";

@Component({
	selector: "app-root",
	templateUrl: "app.component.html",
	styleUrls: ["app.component.scss"],
	standalone: false
})
export class AppComponent extends AppComponentBase implements OnInit {
	//#region FIELDS

	private static readonly C_LOG_ID = "APP.C::";

	//#endregion

	//#region PROPERTIES

	/** Clé du menu sur le côté. */
	public readonly menuKey = "sideMenu";
	/** Titre du menu, si vide, pas de toolbar. */
	public readonly menuTitle = "Merch";

	//#endregion

	//#region METHODS

	constructor(
		private readonly isvcConfig: ConfigService,
		private readonly isvcUiMessage: UiMessageService,
		private readonly ioMenu: MenuController,
		private readonly isvcLoading: LoadingService,
		private readonly isvcFlag: FlagService,
		private readonly isvcPatch: PatchService,
		private readonly isvcLogger: LoggerService,
		private readonly isvcPlatform: PlatformService,
		private readonly isvcPwaUpdate: PwaUpdateService,
		private readonly isvcLogisticsService: LogisticsSettingsService,
		private readonly isvcBarcodeReader: BarcodeReaderService,
		private readonly isvcBackgroundTask: BackgroundTaskService,
		psvcStoreBts: StoreBtsService,
		psvcBattery: BatteryService,
		psvcApplication: ApplicationService
	) {
		super(psvcApplication, isvcPlatform, psvcBattery);

		this.initAsync();

		psvcStoreBts.addReplicateToServerTasks$().pipe(takeUntil(this.destroyed$)).subscribe();
	}

	public ngOnInit(): void {
		let loLoader: Loader | undefined;

		this.ioMenu.swipeGesture(true);
		this.ioMenu.enable(false);

		this.addComponentRegistry();

		constants.environment.version = packageJson.version;

		defer(() => this.isvcLoading.create("Démarrage de l'application ..."))
			.pipe(
				tap((poLoader: Loader) => (loLoader = poLoader)),
				mergeMap((poLoader: Loader) => poLoader.present()),
				mergeMap((_) => this.isvcConfig.init(constants as IConfig)), // Initialisation des configs de l'app.
				mergeMap((_) => this.isvcPatch.applyPatchesAsync()), // Application des patchs.
				tap({
					next: () => {
						loLoader?.dismiss();
						this.isvcFlag.setFlagValue(EFlag.appInitialized, true);
						this.logAppInitialized();
					},
					error: (poError) => this.initError(poError)
				}),
				finalize(() => loLoader?.dismiss())
			)
			.subscribe();

		this.isvcPwaUpdate.initUpdateCheck();
	}

	/** Configure des paramètres de services et les tâches récurentes. */
	private initAsync(): Promise<void> {
		return firstValueFrom(
			this.isvcFlag.waitForFlag(EBTSFlag.BTSDatabaseInitialized, true).pipe(
				mergeMap((_) => this.addArticleCatalogUpdateTask$()),
				mergeMap((_) => this.addRemoveObsoleteCustomerHistoryTask$()),
				// On télécharge / met à jour les stocks uniquement sur mobile.
				mergeMap((_) => (this.isvcPlatform.isMobileApp ? this.addStockUpdateTask() : of(true))),
				mergeMap((_) => {
					if (this.isvcPlatform.isAndroid) {
						return this.isvcFlag.waitForFlag(EStoreFlag.DBInitialized, true).pipe(
							mergeMap((__) => this.isvcLogisticsService.get$()),
							take(1),
							mergeMap((poLogisticsSettings: LogisticsSettings) =>
								this.isvcBarcodeReader.setAccuracyAsync(poLogisticsSettings.barcodeReader.accuracy)
							)
						);
					} else return EMPTY;
				}),
				defaultIfEmpty(undefined),
				takeUntil(this.destroyed$)
			)
		);
	}

	/** Crée la tâche récurente pour mettre à jour le catague article. */
	private addArticleCatalogUpdateTask$(): Observable<boolean> {
		const lsTaskType = "ArticleCatalogUpdateTask";

		this.isvcBackgroundTask.addTaskType(lsTaskType, ArticleCatalogUpdateTask);
		return this.isvcBackgroundTask.addTask$<IArticleCatalogUpdateTaskParams>({
			taskId: `${lsTaskType}Id`,
			description: `Mise à jour du catalogue`,
			taskType: lsTaskType,
			intervalMultiplicator: 1,
			completionInterval: 1_800_000, // 30min
			execAfter: [
				{ key: EStoreFlag.DBInitialized, value: true },
				{ key: ENetworkFlag.isNetworkReliable, value: true }
			]
		});
	}

	/** Crée la tâche récurrente pour supprimer les stocks obsolètes. */
	private addRemoveObsoleteCustomerHistoryTask$(): Observable<boolean> {
		const lsTaskType = "RemoveObsoleteCustomerHistoryTask";

		this.isvcBackgroundTask.addTaskType(lsTaskType, RemoveObsoleteCustomerHistoryTask);
		return this.isvcBackgroundTask.addTask$({
			taskId: `${lsTaskType}Id`,
			description: `Suppression de l'historique des clients obsolètes`,
			taskType: lsTaskType,
			intervalMultiplicator: 1,
			completionInterval: 86_400_000, // 24h == 1x/jour ; même 1x/j c'est largement suffisant vu que les stocks vont pas être mis à jour 10x/j.
			execAfter: [{ key: EStoreFlag.DBInitialized, value: true }]
		});
	}

	/** Crée la tâche récurente pour mettre à jour les stocks. */
	private addStockUpdateTask(): Observable<boolean> {
		const lsTaskType = "StockUpdateTask";
		const loTaskParams: IStockUpdateTaskParams = {};

		this.isvcBackgroundTask.addTaskType(lsTaskType, StockUpdateTask);
		return this.isvcBackgroundTask.addTask$({
			taskId: `${lsTaskType}Id`,
			description: `Mise à jour des stocks`,
			taskType: lsTaskType,
			params: loTaskParams,
			intervalMultiplicator: 1,
			completionInterval: 1_800_000, // 30min
			execAfter: [
				{ key: EStoreFlag.DBInitialized, value: true },
				{ key: ENetworkFlag.isNetworkReliable, value: true },
				{ key: EMerchFlag.obsoleteStocksRemoved, value: true }
			]
		});
	}

	private initError(poError: string | DeviceNotAuthorizedError | any): void {
		console.error(`APP.C::Application initialization error.`, poError);

		//! Nécessaire afin de supprimer le loader : si utilisation de l'instance du loader ou si cette ligne de code est appelée dans le finalize() alors le loader restera.
		this.isvcLoading.dismissAll();

		/** Message d'erreur à afficher à l'utilisateur. */
		let lsMessage: string;

		// Si l'erreur reçu est de type `string`, elle est générée par OSApp, on l'affiche directement à l'utilisateur.
		if (typeof poError === "string") lsMessage = poError;
		// Si c'est un type d'erreur géré par OSApp, on l'affiche.
		else if (poError instanceof DeviceNotAuthorizedError)
			lsMessage = poError.message; // Type d'erreur non géré.
		else lsMessage = "Une erreur critique s'est produite. Veuillez contacter le support technique.";

		this.isvcUiMessage.showPopup(
			new ShowMessageParamsPopup({
				header: "Erreur",
				message: lsMessage,
				buttons: [
					{ text: "Quitter", handler: () => ApplicationService.exitApp() } as AlertButton,
					{
						text: "Réessayer",
						handler: () => {
							ApplicationService.reloadApp();
						}
					} as AlertButton
				]
			})
		);
	}

	private logAppInitialized(): void {
		console.log("APP.C::Application initialized");

		const loAppStartPauseLogData: IAppStartPauseLogData = {
			version: ConfigData.appInfo.appVersion!,
			environment: ConfigData.environment.id
		};

		this.isvcLogger.action(
			AppComponent.C_LOG_ID,
			"Démarrage de l'application.",
			ELogActionId.appStart,
			loAppStartPauseLogData
		);
	}

	private addComponentRegistry(): void {
		ModalComponentsRegistry.add("catalog-modal", CatalogModalMainComponent);
	}

	//#endregion
}
